import type { MeilisearchProduct } from "../types-var/product";

interface CategoryFilter {
  id: number;
  name: string;
  count: number;
  level: number;
  parentId: number | null;
  isHeader?: boolean; // Used for non-clickable Level 3 categories
  children?: CategoryFilter[];
}

// ✅ Builds relevant category filters from Meilisearch products
export const getRelevantCategoryFilters = (products: MeilisearchProduct[]) => {
  const categoryMap = new Map<number, CategoryFilter>();

  // First pass: Collect all categories and count products per category
  products.forEach((product) => {
    product.categories.forEach((cat) => {
      if (!categoryMap.has(cat.id)) {
        categoryMap.set(cat.id, {
          id: cat.id,
          name: cat.name,
          count: 0,
          level: cat.level,
          parentId: cat.parentId ?? null,
          children: [],
        });
      }
      categoryMap.get(cat.id)!.count++;
    });
  });

  // Build category hierarchy while keeping it usable as a filter
  const buildHierarchy = () => {
    const level1Categories: CategoryFilter[] = [];
    const level2Map = new Map<number, CategoryFilter>();
    const level3ByParent = new Map<number, CategoryFilter[]>();
    const level4ByParent = new Map<number, CategoryFilter[]>();

    // Organize categories by level
    categoryMap.forEach((cat) => {
      if (cat.level === 1) {
        level1Categories.push(cat);
      } else if (cat.level === 2) {
        level2Map.set(cat.id, {
          ...cat,
          name: `Ver todo ${cat.name}`, // ✅ "Ver todo" prefix for Level 2
          children: [],
        });
      } else if (cat.level === 3) {
        const parentArray = level3ByParent.get(cat.parentId!) || [];
        parentArray.push({ ...cat, isHeader: true, children: [] }); // ✅ Level 3 is a subtitle
        level3ByParent.set(cat.parentId!, parentArray);
      } else if (cat.level === 4) {
        const parentArray = level4ByParent.get(cat.parentId!) || [];
        parentArray.push(cat);
        level4ByParent.set(cat.parentId!, parentArray);
      }
    });

    // ✅ Connect Level 3 to Level 4
    level3ByParent.forEach((level3Categories, parentId) => {
      level3Categories.forEach((l3Cat) => {
        l3Cat.children = level4ByParent.get(l3Cat.id) || [];
      });
    });

    // ✅ Connect Level 2 to Level 3
    return Array.from(level2Map.values()).map((l2Cat) => {
      const level3Children = level3ByParent.get(l2Cat.id) || [];
      return {
        ...l2Cat,
        children: level3Children, // ✅ Now includes Level 3
      };
    });
  };

  return buildHierarchy();
};

export const extractFiltersFromProducts = (products: MeilisearchProduct[]) => {
  const filters: { [key: string]: Set<any> } = {};
  const styles: { [key: string]: string } = {};
  const counts: { [key: string]: { [value: string]: number } } = {};

  // Initialize counts
  const initializeCount = (key: string, value: string) => {
    if (!counts[key]) counts[key] = {};
    if (!counts[key][value]) counts[key][value] = 0;
    counts[key][value]++;
  };

  products.forEach((product) => {
    product.attributes.forEach((attr) => {
      if (!attr.filterShow) return;

      if (!filters[attr.name]) {
        filters[attr.name] = new Set();
        if (attr.gropingAttributeStyle) {
          styles[attr.name] = attr.gropingAttributeStyle;
        }
      }

      attr.values.forEach((value) => {
        if (value.isAvailable) {
          let filterValue = value.name;
          if (attr.gropingAttributeStyle === "colorSwatch") {
            filterValue = JSON.stringify({
              name: value.name,
              colorHex: value.colorHex,
            });
          }
          filters[attr.name].add(filterValue);
          initializeCount(attr.name, filterValue);
        }
      });
    });
  });

  // ✅ Add hierarchical categories
  // ✅ Ensure category filters are properly added

  const categoryFilters = getRelevantCategoryFilters(products);

  // ✅ Ensure category filters include Level 3 & 4 categories, not just Level 2
  if (categoryFilters.length > 0) {
    filters["Categorías"] = new Set();

    categoryFilters.forEach((l2Cat) => {
      // Calculate Level 2 count correctly - only count direct products
      filters["Categorías"].add(
        JSON.stringify({
          id: l2Cat.id,
          name: l2Cat.name, // Remove "Ver todo" prefix here
          count: l2Cat.count, // Use direct count instead of total
          level: 2,
          parentId: l2Cat.parentId,
        })
      );

      if (l2Cat.children) {
        l2Cat.children.forEach((l3Cat) => {
          // Add Level 3 categories
          filters["Categorías"].add(
            JSON.stringify({
              id: l3Cat.id,
              name: l3Cat.name,
              count: l3Cat.count,
              level: 3,
              parentId: l3Cat.parentId,
              isHeader: true,
            })
          );

          if (l3Cat.children) {
            l3Cat.children.forEach((l4Cat) => {
              // Add Level 4 categories
              filters["Categorías"].add(
                JSON.stringify({
                  id: l4Cat.id,
                  name: l4Cat.name,
                  count: l4Cat.count,
                  level: 4,
                  parentId: l4Cat.parentId,
                })
              );
            });
          }
        });
      }
    });
  }

  console.log("Extracted Filters:", filters);

  return {
    filters: Object.fromEntries(
      Object.entries(filters).map(([key, values]) => [key, Array.from(values)])
    ),
    styles,
    counts,
  };
};
